import React, { createContext, useCallback, useContext, useState } from 'react';
import { Modal } from './Modal';

interface ConfirmOptions {
  title?: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

interface PendingConfirm extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

/**
 * Replaces window.confirm with an in-brand dialog. Usage:
 *   const confirm = useConfirm();
 *   if (await confirm({ description: '...' })) { ... }
 */
export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      setPending({ ...options, resolve });
    });
  }, []);

  const handleClose = (result: boolean) => {
    pending?.resolve(result);
    setPending(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && (
        <Modal
          open
          onClose={() => handleClose(false)}
          title={pending.title || 'Tasdiqlash'}
          size="sm"
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => handleClose(false)}>
                {pending.cancelLabel || 'Bekor qilish'}
              </button>
              <button
                className={pending.danger ? 'btn btn-danger' : 'btn btn-primary'}
                onClick={() => handleClose(true)}
                autoFocus
              >
                {pending.confirmLabel || 'Tasdiqlash'}
              </button>
            </>
          }
        >
          <p className="text-sm text-gray-600">{pending.description}</p>
        </Modal>
      )}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = (): ConfirmFn => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx;
};
